import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { Pack, PackStats, PokemonCard, UserCard } from '../types/pokemon';
import { useAuth } from './useAuth';

export function usePackStats(setCode: string | null | undefined) {
  const [packs, setPacks] = useState<Pack[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    const fetchPackStats = async () => {
      if (!setCode || !user) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        const { data: packData, error: packError } = await supabase
          .from('packs')
          .select('*')
          .eq('set', setCode);

        if (packError) throw packError;
        setPacks(packData || []);
      } catch (err) {
        console.error('Error fetching pack statistics:', err);
        setError(err instanceof Error ? err.message : 'Une erreur est survenue');
        setPacks([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPackStats();
  }, [setCode, user?.id]);

  const calculatePackRecommendations = (cards: PokemonCard[], collection: Record<number, UserCard>) => {
    if (!cards || cards.length === 0 || !packs.length) return [];

    try {
      // Trouver les cartes "All"
      const allCards = cards.filter(card => 
        card.pack?.toLowerCase() === 'all' || 
        card.pack?.toLowerCase() === 'all'
      );

      // Calculer les statistiques pour chaque pack du set
      const packStats = packs.map(packInfo => {
        // Filtrer les cartes spécifiques à ce pack + les cartes "All"
        const packCards = [
          ...cards.filter(card => card.pack === packInfo.name),
          ...allCards
        ];
        
        // Filtrer les cartes qui ne sont pas dans la collection ou qui ont une quantité de 0
        const missingCards = packCards.filter(card => {
          const userCard = collection[card.id];
          return !userCard || userCard.quantity === 0;
        });

        // Calculer le pourcentage de cartes manquantes
        const totalCards = packCards.length;
        const missingPercentage = totalCards > 0 ? (missingCards.length / totalCards) * 100 : 0;

        return {
          pack: packInfo.name,
          totalCards,
          missingCards: missingCards.length,
          missingPercentage,
          missingByRarity: missingCards.reduce((acc, card) => {
            if (!acc[card.rarity]) {
              acc[card.rarity] = 0;
            }
            acc[card.rarity]++;
            return acc;
          }, {} as Record<string, number>),
          packId: packInfo.id,
          packImage: packInfo.img
        };
      });

      // Trier les packs par pourcentage de cartes manquantes (décroissant)
      return packStats
        .filter(pack => pack.totalCards > 0) // Ne garder que les packs qui ont des cartes
        .sort((a, b) => b.missingPercentage - a.missingPercentage);
    } catch (error) {
      console.error('Error calculating pack recommendations:', error);
      return [];
    }
  };

  return { packs, loading, error, calculatePackRecommendations };
}